import { buildRamp, lighten, readableForeground } from './color-ramp';
import { DEFAULT_ACCENT, DEFAULT_PRIMARY } from './brand-colors';

/**
 * FR7 — Writes a tenant's palette over the platform's design tokens.
 *
 * An injected stylesheet scoped to `:root:not(.dark)`, not inline styles:
 * inline would beat `.dark { … }` in the cascade and repaint dark mode with
 * light-mode colours.
 */

const STYLE_ID = 'tenant-branding';
const CACHE_KEY_PREFIX = 'minetech.branding.';

export interface TenantColors {
  primary: string;
  accent: string;
}

/**
 * The generated ramp for the platform's own colours only approximates the
 * hand-tuned scale in styles/index.css, so a default palette injects nothing
 * and lets the real stylesheet stand.
 */
const isPlatformPalette = ({ primary, accent }: TenantColors) =>
  primary.toLowerCase() === DEFAULT_PRIMARY.toLowerCase() &&
  accent.toLowerCase() === DEFAULT_ACCENT.toLowerCase();

/** Keyed by hostname so one workspace's palette never paints another's origin. */
const cacheKey = (hostname: string) => CACHE_KEY_PREFIX + hostname;

function currentHost(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

function buildCss(colors: TenantColors): string | null {
  const ramp = buildRamp(colors.primary);
  if (!ramp) return null;

  const declarations = [
    `--color-primary:${colors.primary}`,
    `--color-primary-foreground:${readableForeground(colors.primary)}`,
    ...Object.entries(ramp).map(([step, hex]) => `--color-primary-${step}:${hex}`),
    // The platform pairs these with the palest and a deep step of the ramp;
    // keeping that relationship means hover surfaces stay tinted, not grey.
    `--color-accent:${ramp[50]}`,
    `--color-accent-foreground:${ramp[700]}`,
    `--color-brand-accent:${colors.accent}`,
    `--color-brand-accent-light:${lighten(colors.accent)}`,
    // Missing any one leaves an island of the platform's colour — links and
    // breadcrumbs, the active nav item, or every focus ring.
    `--color-text-action:${colors.primary}`,
    `--color-ring:${colors.primary}`,
    `--color-sidebar-accent:${ramp[50]}`,
    `--color-sidebar-accent-foreground:${colors.primary}`,
  ];

  // Semantic colours are deliberately absent: a tenant whose brand is red must
  // not get green danger buttons.
  return `:root:not(.dark){${declarations.join(';')}}`;
}

export function applyTenantColors(colors: TenantColors): void {
  if (typeof document === 'undefined') return;

  if (isPlatformPalette(colors)) {
    clearTenantColors();
    return;
  }

  const css = buildCss(colors);
  if (!css) return;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  if (style.textContent !== css) style.textContent = css;

  try {
    localStorage.setItem(cacheKey(currentHost()), JSON.stringify(colors));
  } catch {
    // Private browsing or a full quota; the next load just repaints a moment late.
  }
}

/**
 * Repaints from the last known palette before React mounts. The real colours
 * are a round-trip away, so without this every load flashes the platform's
 * brown first.
 */
export function bootTenantColors(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(cacheKey(currentHost()));
    if (!raw) return;
    const cached = JSON.parse(raw) as unknown;
    const { primary, accent } = cached as Partial<TenantColors>;
    if (primary && accent) applyTenantColors({ primary, accent });
  } catch {
    // A malformed entry is not worth blocking boot over.
  }
}

/** Drops the override, so the platform's own tokens apply again. */
export function clearTenantColors(): void {
  document.getElementById(STYLE_ID)?.remove();
  try {
    localStorage.removeItem(cacheKey(currentHost()));
  } catch {
    /* empty */
  }
}

/**
 * Reads a design token's current value, for anything rendered outside the
 * document — a print window at a `blob:` URL, a canvas — where the stylesheet
 * does not reach and the colour has to be inlined.
 */
export function readToken(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}
